if (!customElements.get('ondelie-essentials')) {
  customElements.define(
    'ondelie-essentials',
    class OndelieEssentials extends HTMLElement {
      constructor() {
        super();
        this.observer = null;
        this.handleClick = this.handleClick.bind(this);
        this.handleBundleSubmit = this.handleBundleSubmit.bind(this);
      }

      connectedCallback() {
        this.classList.add('is-mounted');
        this.addEventListener('click', this.handleClick);
        this.bundleForms = Array.from(this.querySelectorAll('[data-oe-bundle-form]'));
        this.bundleForms.forEach((form) => form.addEventListener('submit', this.handleBundleSubmit));

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.classList.add('is-visible');
          return;
        }

        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(
            (entries) => {
              if (!entries[0].isIntersecting) return;
              this.classList.add('is-visible');
              this.observer.disconnect();
            },
            {rootMargin: '0px 0px -8% 0px', threshold: 0.12}
          );
          this.observer.observe(this);
        } else {
          this.classList.add('is-visible');
        }
      }

      handleClick(event) {
        const variant = event.target.closest('[data-oe-variant]');
        const purchase = event.target.closest('[data-oe-purchase]');
        const plan = event.target.closest('[data-oe-plan]');
        const bundleMode = event.target.closest('[data-oe-bundle-mode]');

        if (variant && this.contains(variant)) this.selectVariant(variant);
        if (purchase && this.contains(purchase)) this.selectPurchaseMode(purchase);
        if (plan && this.contains(plan)) this.selectPlan(plan);
        if (bundleMode && this.contains(bundleMode)) this.selectBundleMode(bundleMode);
      }

      selectVariant(selected) {
        const card = selected.closest('[data-oe-card]');
        if (!card) return;

        const buttons = Array.from(card.querySelectorAll('[data-oe-variant]'));
        const input = card.querySelector('[data-oe-variant-input]');
        const image = card.querySelector('[data-oe-product-image]');

        this.setPressed(buttons, selected);
        if (input) input.value = selected.dataset.variantId;

        if (image && selected.dataset.image) {
          image.classList.add('is-changing');
          window.setTimeout(() => {
            image.removeAttribute('srcset');
            image.src = selected.dataset.image;
            image.alt = selected.dataset.imageAlt || image.alt;
            image.classList.remove('is-changing');
          }, 150);
        }

        if (card.querySelector('[data-oe-bundle-picker]')) {
          this.updateBundle(card, selected);
          return;
        }

        const price = card.querySelector('[data-oe-price]');
        const compare = card.querySelector('[data-oe-compare]');
        const addButton = card.querySelector('[data-add-to-cart]');
        const addText = card.querySelector('[data-add-to-cart-text]');
        const isAvailable = selected.dataset.available === 'true';

        if (price) price.textContent = selected.dataset.price;
        this.updateCompare(compare, selected.dataset.compare);

        if (addButton) addButton.disabled = !isAvailable;
        if (addText) {
          addText.textContent = isAvailable ? addText.dataset.availableText : addText.dataset.soldOutText;
        }
      }

      selectBundleMode(selected) {
        const card = selected.closest('[data-oe-card]');
        if (!card) return;
        this.setPressed(Array.from(card.querySelectorAll('[data-oe-bundle-mode]')), selected);
        this.updateBundle(card);
      }

      updateBundle(card, selectedVariant = null) {
        const variant =
          selectedVariant || card.querySelector('[data-oe-variant].is-selected') || card.querySelector('[data-oe-variant-input]');
        const selectedMode = card.querySelector('[data-oe-bundle-mode].is-selected');
        const isSubscription = selectedMode?.dataset.mode === 'subscription';
        const options = Array.from(card.querySelectorAll('[data-oe-bundle-mode]'));

        if (!variant || !selectedMode) return;

        options.forEach((option) => {
          const subscriptionOption = option.dataset.mode === 'subscription';
          const optionPrice = option.querySelector('[data-oe-option-price]');
          const optionCompare = option.querySelector('[data-oe-option-compare]');
          const value = subscriptionOption ? variant.dataset.subPrice : variant.dataset.onePrice;
          const compareValue = subscriptionOption ? variant.dataset.subCompare : variant.dataset.oneCompare;

          if (optionPrice && value) optionPrice.textContent = value;
          this.updateCompare(optionCompare, compareValue);
        });

        const filterVariant = card.querySelector('[data-oe-filter-variant]')?.value;
        const filterPlan = card.querySelector('[data-oe-filter-plan]')?.value;
        const filterAvailable = card.querySelector('[data-oe-filter-available]')?.value === 'true';
        const primaryAvailable = variant.dataset.available === 'true';
        const available = primaryAvailable && (!isSubscription || (filterVariant && filterPlan && filterAvailable));
        const button = card.querySelector('.oe-bundle-submit');
        const buttonLabel = card.querySelector('[data-oe-button-label]');
        const buttonPrice = card.querySelector('[data-oe-button-price]');
        const activePrice = isSubscription ? variant.dataset.subPrice : variant.dataset.onePrice;

        if (button) button.disabled = !available;
        if (buttonLabel && button) {
          buttonLabel.textContent = available ? button.dataset.availableText : button.dataset.soldOutText;
        }
        if (buttonPrice && activePrice) buttonPrice.textContent = activePrice;
      }

      async handleBundleSubmit(event) {
        event.preventDefault();

        const form = event.currentTarget;
        const card = form.closest('[data-oe-card]');
        const button = form.querySelector('.oe-bundle-submit');
        const error = form.querySelector('[data-cart-errors-container]');
        const primaryVariant = form.querySelector('[data-oe-variant-input]')?.value;
        const selectedMode = form.querySelector('[data-oe-bundle-mode].is-selected')?.dataset.mode;
        const filterVariant = form.querySelector('[data-oe-filter-variant]')?.value;
        const filterPlan = form.querySelector('[data-oe-filter-plan]')?.value;

        if (!primaryVariant || !button || button.disabled) return;

        const items = [{id: Number(primaryVariant), quantity: 1}];
        if (selectedMode === 'subscription' && filterVariant && filterPlan) {
          items.push({id: Number(filterVariant), quantity: 1, selling_plan: Number(filterPlan)});
        }

        button.classList.add('is-loading');
        button.disabled = true;
        if (error) error.textContent = '';

        try {
          const response = await fetch(window.theme?.routes?.cart_add_url || '/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
              Accept: 'application/javascript',
            },
            body: JSON.stringify({items}),
          });
          const result = await response.json();

          if (!response.ok || result.status) {
            throw new Error(result.description || result.message || 'Unable to add this ritual to the bag.');
          }

          document.dispatchEvent(new CustomEvent('theme:cart:refresh', {bubbles: true}));
          document.dispatchEvent(new CustomEvent('theme:cart:added', {bubbles: true}));
        } catch (requestError) {
          if (error) error.textContent = requestError.message;
        } finally {
          button.classList.remove('is-loading');
          if (card) this.updateBundle(card);
        }
      }

      selectPurchaseMode(selected) {
        const card = selected.closest('[data-oe-card]');
        if (!card) return;

        const buttons = Array.from(card.querySelectorAll('[data-oe-purchase]'));
        const planPicker = card.querySelector('[data-oe-plan-picker]');
        const sellingPlanInput = card.querySelector('[data-oe-selling-plan]');
        const isSubscription = selected.dataset.mode === 'subscription';

        this.setPressed(buttons, selected);
        if (planPicker) planPicker.hidden = !isSubscription;

        if (isSubscription) {
          const activePlan = card.querySelector('[data-oe-plan].is-selected') || card.querySelector('[data-oe-plan]');
          if (activePlan) this.applyPlan(card, activePlan);
        } else {
          if (sellingPlanInput) {
            sellingPlanInput.disabled = true;
            sellingPlanInput.value = '';
          }
          this.updatePrice(card, selected.dataset.price, '');
        }
      }

      selectPlan(selected) {
        const card = selected.closest('[data-oe-card]');
        if (!card) return;
        this.setPressed(Array.from(card.querySelectorAll('[data-oe-plan]')), selected);
        this.applyPlan(card, selected);
      }

      applyPlan(card, selected) {
        const sellingPlanInput = card.querySelector('[data-oe-selling-plan]');
        if (sellingPlanInput) {
          sellingPlanInput.disabled = false;
          sellingPlanInput.value = selected.dataset.planId;
        }
        this.updatePrice(card, selected.dataset.price, selected.dataset.compare);
      }

      updatePrice(card, value, compareValue) {
        const price = card.querySelector('[data-oe-price]');
        const compare = card.querySelector('[data-oe-compare]');
        if (price && value) price.textContent = value;
        this.updateCompare(compare, compareValue);
      }

      updateCompare(element, value) {
        if (!element) return;
        element.textContent = value || '';
        element.hidden = !value;
      }

      setPressed(buttons, selected) {
        buttons.forEach((button) => {
          const isSelected = button === selected;
          button.classList.toggle('is-selected', isSelected);
          button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        });
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);
        this.bundleForms?.forEach((form) => form.removeEventListener('submit', this.handleBundleSubmit));
        if (this.observer) this.observer.disconnect();
      }
    }
  );
}
